import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { ButtonComponent } from '@/app/features/basic-components/button/button';
import { PUBLIC_NAVBAR_ICONS } from './public-navbar.icons';
import { PublicNavbarFacade } from '@/app/layouts/public-layout/public-navbar.facade';
import { CartStorageService } from '@/app/features/shared/cart/services/cart-storage.service';
import { PrivateNavbarFacade } from '@/app/layouts/private-layout/navbar/private-navbar.facade';
import { PrivateNavbarMenuComponent, PrivateNavbarMenuAction } from '@/app/layouts/private-layout/menu/menu';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    RouterLinkActive,
    TranslateModule,
    ButtonComponent,
    PrivateNavbarMenuComponent,
  ],
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
})
export class NavbarComponent implements AfterViewInit, OnDestroy {
  private readonly cartStorageService = inject(CartStorageService);

  @ViewChild('navbarRoot', { static: true }) navbarRoot!: ElementRef<HTMLElement>;

  readonly icons = PUBLIC_NAVBAR_ICONS;
  readonly cartItemCount = this.cartStorageService.cartItemCount;
  readonly cartBadgeText = computed(() => {
    const count = this.cartItemCount();

    return count > 99 ? '99+' : `${count}`;
  });

  readonly menuOpen$: Observable<boolean>;

  searchValue = '';
  navbarHeightPx = 0;
  private ro?: ResizeObserver;

  constructor(
    public facade: PublicNavbarFacade,
    public menuFacade: PrivateNavbarFacade,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {
    this.menuOpen$ = this.menuFacade.menuOpen$;
    effect(() => {
      this.facade.searchClearedAt();
      this.searchValue = '';
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    const el = this.navbarRoot.nativeElement;

    const update = () => {
      this.navbarHeightPx = el.offsetHeight || 0;
      this.cdr.markForCheck();
    };

    update();

    this.ro = new ResizeObserver(() => update());
    this.ro.observe(el);
  }

  ngOnDestroy(): void {
    this.ro?.disconnect();
  }

  onMenuAction(action: PrivateNavbarMenuAction): void {
    this.menuFacade.handleMenuAction(action);
  }

  onCotizarClick(): void {
    this.facade.searchOrQuote(this.searchValue);
  }

  onSearchChange(value: string): void {
    this.searchValue = value ?? '';
  }

  async pasteLinkFromClipboard(): Promise<void> {
    const pastedValue = await this.readClipboardText();

    if (!pastedValue) {
      return;
    }

    this.ngZone.run(() => {
      this.onSearchChange(pastedValue);
      this.cdr.detectChanges();
    });
  }

  private async readClipboardText(): Promise<string> {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) {
      return '';
    }

    try {
      return (await navigator.clipboard.readText()).trim();
    } catch {
      return '';
    }
  }
}
